import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";
import { graphqlRequest } from "../Graphql/graphqlClient";
import { TASK_GET_TASKS_V2 } from "../Graphql/queries/taskQueries";

const useTasks = () => {
  const {
    api,
    bearerApi,
    AdminId,
    apiGraphqlUrl,
    GraphToken,
    handleApiResponseCode,
  } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "01" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const handleGraphqlApiResponse = (responseData) => {
    if (!responseData) return false;

    if (
      responseData?.responseCode === "02" ||
      responseData?.responseCode === "03" ||
      responseData?.responseCode === "98" ||
      responseData?.responseCode === "401"
    ) {
      handleApiResponseCode?.(responseData);
      return true;
    }

    return false;
  };

  // ===== GET TASKS - GRAPHQL =====
  const fetchTasks = useCallback(
    async (fromDate, toDate, setTasks, options = {}) => {
      setLoading(true);

      try {
        if (!apiGraphqlUrl || !GraphToken) {
          toast.error("GraphQL configuration missing");
          return [];
        }

        const {
          pageSize = 50,
          nextToken = "",
          previousToken = "",
          status = ["all"],
          driverId = "",
          setPageInfo,
          mode = "replace",
        } = options;

        const variables = {
          req: {
            adminId: AdminId || "",
            driverId,
            fromDate,
            toDate,
            status,
            pageSize,
            nextToken,
            previousToken,
          },
        };

        const data = await graphqlRequest({
          url: apiGraphqlUrl,
          query: TASK_GET_TASKS_V2,
          variables,
          token: GraphToken,
          authType: "Basic",
        });

        const taskResponse = data?.taskGetTasksV2;

        if (handleGraphqlApiResponse(taskResponse)) {
          if (mode !== "append") {
            setTasks([]);
          }
          return [];
        }

        if (taskResponse?.responseCode === "00") {
          const newTasks = taskResponse.data || [];

          if (mode === "append") {
            setTasks((prev) => {
              const existingIds = new Set(
                (prev || []).map((task) => String(task.pk_task_id))
              );

              const uniqueNewTasks = newTasks.filter(
                (task) => !existingIds.has(String(task.pk_task_id))
              );

              return [...(prev || []), ...uniqueNewTasks];
            });
          } else {
            setTasks(newTasks);
          }

          if (typeof setPageInfo === "function") {
            setPageInfo({
              nextToken: taskResponse.info?.nextToken || "",
              previousToken: taskResponse.info?.previousToken || "",
              pageSize: Number(taskResponse.info?.pageSize || pageSize),
              totalCount: Number(taskResponse.info?.totalCount || 0),
              pageNumber: Number(taskResponse.info?.pageNumber || 1),
            });
          }

          return newTasks;
        }

        if (shouldShowLocalError(taskResponse)) {
          toast.error(taskResponse?.responseMessage || "Failed to fetch tasks");
        }

        if (mode !== "append") {
          setTasks([]);
        }

        return [];
      } catch (error) {
        console.error("Error fetching tasks:", error);
        toast.error(error?.message || "Error fetching tasks");

        if (options?.mode !== "append") {
          setTasks([]);
        }

        return [];
      } finally {
        setLoading(false);
      }
    },
    [apiGraphqlUrl, GraphToken, AdminId, handleApiResponseCode]
  );

  // ===== CHANGE TASK STATUS =====
  // Original API used Basic token, so use api
  const changeTaskStatus = async (task, newStatus, refresh) => {
    if (!task || !newStatus) return false;

    setLoading(true);

    try {
      const payload = {
        taskId: task.pk_task_id.toString(),
        status: newStatus,
        driverId: task.driver_id?.toString() || "0",
        adminId: AdminId,
      };

      const res = await api.post(
        "/api/Task/v1.0/changeTaskStatusByPackky",
        payload
      );

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Status updated.");

        if (typeof refresh === "function") {
          await refresh();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to update status.");
      }

      return false;
    } catch (error) {
      console.error("changeTaskStatus error:", error);
      toast.error("Error changing task status.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ===== DELETE TASK =====
  // Original API used Bearer token, so use bearerApi
  const deleteTask = async (taskId, refresh) => {
    if (!taskId) {
      toast.error("Invalid task ID");
      return false;
    }

    try {
      const res = await bearerApi.post("/api/Task/v1.0/deleteTask", {
        taskId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Task deleted");

        if (typeof refresh === "function") {
          await refresh();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to delete task");
      }

      return false;
    } catch (error) {
      console.error("deleteTask error:", error);
      toast.error("Error while deleting task");
      return false;
    }
  };

  // ===== TASK LOGS =====
  // Original APIs used Bearer token, so use bearerApi
  const fetchTaskLogs = async (taskId, fromDate, toDate) => {
    if (!taskId) {
      return {
        assignmentLogs: [],
        statusChangeLogs: [],
      };
    }

    const payload = {
      fromDate,
      toDate,
      taskId: taskId.toString(),
      adminId: AdminId,
    };

    try {
      const [assignRes, statusRes] = await Promise.all([
        bearerApi.post("/api/Task/v1.0/getTaskAssignmentLogs", payload),
        bearerApi.post("/api/Task/v1.0/getTaskStatusChangeLogs", payload),
      ]);

      return {
        assignmentLogs:
          assignRes.data?.responseCode === "00"
            ? assignRes.data?.data || []
            : [],

        statusChangeLogs:
          statusRes.data?.responseCode === "00"
            ? statusRes.data?.data || []
            : [],
      };
    } catch (err) {
      console.error("fetchTaskLogs error:", err);
      toast.error("Error fetching task logs.");

      return {
        assignmentLogs: [],
        statusChangeLogs: [],
      };
    }
  };

  // ===== REQUEST LOCATION =====
  // Original API used Basic token, so use api
  const requestLocation = async (orderId, refresh) => {
    if (!orderId) {
      toast.error("Invalid order ID");
      return false;
    }

    try {
      const res = await api.post("/api/Task/v1.0/locationRequest", {
        orderId,
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Location requested");

        if (typeof refresh === "function") {
          await refresh();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to request location");
      }

      return false;
    } catch (error) {
      console.error("requestLocation error:", error);
      toast.error("Error requesting location");
      return false;
    }
  };

  // ===== GET TASK DETAILS =====
  // Original API used Basic token, so use api
  const fetchTaskDetails = async (taskId) => {
    if (!taskId) return null;

    setLoading(true);

    try {
      const payload = {
        deviceId: "",
        taskId: taskId.toString(),
      };

      const res = await api.post("/api/Task/v1.0/getTaskDetails", payload);

      if (res.data?.responseCode === "00") {
        return res.data?.data || res.data?.response || null;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to fetch task details");
      }

      return null;
    } catch (error) {
      console.error("fetchTaskDetails error:", error);
      toast.error("Error fetching task details");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ===== TASK GLOBAL SEARCH =====
  // Original API used Basic token, so use api
  const taskGlobalSearch = async ({ word, fromDate, toDate }) => {
    if (!word) {
      toast.error("Please enter search id");
      return [];
    }

    setGlobalSearchLoading(true);

    try {
      const payload = {
        word: word.toString(),
        fromDate,
        toDate,
      };

      const res = await api.post("/api/Task/v1.0/taskGlobalSearch", payload);

      if (res?.data?.responseCode === "00") {
        return res?.data?.data || res?.data?.response || [];
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "No data found");
      }

      return [];
    } catch (error) {
      console.error("taskGlobalSearch error:", error);
      toast.error("Error while searching task");
      return [];
    } finally {
      setGlobalSearchLoading(false);
    }
  };

  return {
    fetchTasks,
    changeTaskStatus,
    deleteTask,
    fetchTaskLogs,
    requestLocation,
    fetchTaskDetails,
    taskGlobalSearch,

    loading,
    globalSearchLoading,
  };
};

export default useTasks;